import { useMemo, useState, useEffect } from 'react';
import { cn, formatMoney, formatDate, formatHours } from '../lib/utils';
import { Loader2, Pencil, Save, X } from 'lucide-react';
import { ICON, Notice } from './ui';

/**
 * Per-categorie tabel-view voor geparseerde document-data.
 * Ondersteunt inline editing (amber markering voor low-confidence velden).
 *
 * Inputs:
 *  - category: loonstrook | urenspec | urencontrole | contract | myrostar | ...
 *  - parsed: raw parsed_json van doc
 *  - docConfidence: parse_confidence van het document zelf ('low' → alle velden amber)
 *  - onSave: callback die nieuwe parsed_json ontvangt en naar backend stuurt
 */
interface Props {
  category: string;
  parsed: any;
  docConfidence: string | null;
  onSave: (newJson: any, note?: string) => Promise<void> | void;
  saving?: boolean;
}

export function ParsedDataTable({ category, parsed, docConfidence, onSave, saving }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<any>(null);

  // Sync draft when parsed changes or edit-mode toggles
  useEffect(() => {
    if (editing) setDraft(structuredClone(parsed));
  }, [editing, parsed]);

  const allLow = docConfidence === 'low';

  const start = () => { setDraft(structuredClone(parsed)); setEditing(true); };
  const cancel = () => { setDraft(null); setEditing(false); };
  const commit = async () => {
    await onSave(draft, 'manual edit via UI');
    setEditing(false);
  };

  if (!parsed) {
    return <p className="text-sm text-slate-400">Er zijn nog geen gegevens uit dit document gehaald.</p>;
  }

  const data = editing && draft ? draft : parsed;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3>Gegevens uit document</h3>
        {!editing ? (
          <button type="button" onClick={start} className="btn-secondary btn-sm">
            <Pencil className={ICON.sm} aria-hidden /> Corrigeren
          </button>
        ) : (
          <div className="flex gap-2">
            <button type="button" onClick={cancel} disabled={saving} className="btn-ghost btn-sm">
              <X className={ICON.sm} aria-hidden /> Annuleren
            </button>
            <button type="button" onClick={commit} disabled={saving} className="btn-primary btn-sm">
              {saving ? <Loader2 className={cn(ICON.sm, 'animate-spin')} aria-hidden /> : <Save className={ICON.sm} aria-hidden />}
              Opslaan
            </button>
          </div>
        )}
      </div>

      {allLow && !editing && (
        <Notice tone="warn">Niet alles is zeker herkend. Vergelijk de gemarkeerde waarden met het document.</Notice>
      )}

      {category === 'loonstrook' && (
        <LoonstrookView data={data} editing={editing} setData={setDraft} allLow={allLow} />
      )}
      {category === 'urenspec' && (
        <UrenspecView data={data} editing={editing} setData={setDraft} allLow={allLow} />
      )}
      {category === 'urencontrole' && (
        <UrencontroleView data={data} editing={editing} setData={setDraft} allLow={allLow} />
      )}
      {(category === 'contract' || category === 'contract_addendum' ||
        category === 'uren_aanpassing' || category === 'functiewijziging') && (
        <ContractView data={data} editing={editing} setData={setDraft} allLow={allLow} />
      )}
      {category === 'myrostar' && (
        <MyrostarView data={data} editing={editing} setData={setDraft} allLow={allLow} />
      )}
      {!['loonstrook', 'urenspec', 'urencontrole', 'contract', 'contract_addendum',
        'uren_aanpassing', 'functiewijziging', 'myrostar'].includes(category) && (
        <GenericJsonView data={data} editing={editing} setData={setDraft} />
      )}
    </div>
  );
}

/* ==================================================================== */
/* Primitives                                                            */
/* ==================================================================== */

interface FieldProps {
  value: any;
  editing: boolean;
  onChange: (v: any) => void;
  low?: boolean;
  type?: 'text' | 'number' | 'date';
  align?: 'left' | 'right';
  formatter?: (v: any) => string;
  placeholder?: string;
}

function Field({ value, editing, onChange, low, type = 'text', align = 'left', formatter, placeholder }: FieldProps) {
  const display = value == null || value === '' ? '—' : (formatter ? formatter(value) : String(value));
  if (!editing) {
    return (
      <span className={cn(
        'inline-block tabular-nums',
        low && 'bg-amber-400/10 text-amber-100 rounded px-1',
        align === 'right' && 'text-right'
      )}>
        {display}
      </span>
    );
  }
  return (
    <input
      type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
      step={type === 'number' ? 'any' : undefined}
      value={value ?? ''}
      placeholder={placeholder}
      onChange={(e) => {
        const v = e.target.value;
        if (type === 'number') {
          onChange(v === '' ? null : Number(v));
        } else {
          onChange(v === '' ? null : v);
        }
      }}
      className={cn(
        'w-full min-w-[4.5rem] rounded-lg px-2 py-1 text-sm bg-slate-950/60 border border-surface-line text-slate-100 focus:border-brand-400 focus:outline-none tabular-nums',
        align === 'right' && 'text-right',
        low && 'border-amber-400/50'
      )}
    />
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[120px_1fr] sm:grid-cols-[160px_1fr] gap-3 items-center py-2 border-b border-surface-line/60 last:border-0">
      <div className="text-sm text-slate-400">{label}</div>
      <div className="text-sm text-slate-100 min-w-0">{children}</div>
    </div>
  );
}

function SectionLabel({ title, count }: { title: string; count: number }) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <h3>{title}</h3>
      <span className="badge-count">{count}</span>
    </div>
  );
}

/* ==================================================================== */
/* Loonstrook                                                            */
/* ==================================================================== */

function LoonstrookView({ data, editing, setData, allLow }: {
  data: any; editing: boolean; setData: (d: any) => void; allLow: boolean;
}) {
  const set = (patch: Partial<any>) => setData({ ...data, ...patch });
  const setComponent = (idx: number, patch: Partial<any>) => {
    const components = [...(data.components ?? [])];
    components[idx] = { ...components[idx], ...patch };
    setData({ ...data, components });
  };
  const components = data.components ?? [];

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-surface-line px-3">
        <Row label="Periode">
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <Field value={data.period_number} editing={editing} onChange={(v) => set({ period_number: v })} type="number" low={allLow} />
            <span className="text-slate-500">van</span>
            <Field value={data.year} editing={editing} onChange={(v) => set({ year: v })} type="number" low={allLow} />
          </span>
          {data.is_correction && <span className="ml-2 status-warn">Correctie</span>}
        </Row>
        <Row label="Verloonde uren">
          <Field value={data.verloonde_uren} editing={editing} onChange={(v) => set({ verloonde_uren: v })} type="number" low={allLow}
                 formatter={(v) => formatHours(Number(v))} />
        </Row>
        <Row label="Brutoloon">
          <Field value={data.brutoloon} editing={editing} onChange={(v) => set({ brutoloon: v })} type="number" low={allLow}
                 formatter={(v) => formatMoney(Number(v))} />
        </Row>
        <Row label="Uitbetaald">
          <Field value={data.uitbetaald} editing={editing} onChange={(v) => set({ uitbetaald: v })} type="number" low={allLow}
                 formatter={(v) => formatMoney(Number(v))} />
        </Row>
        <Row label="Reiskosten onbelast">
          <Field value={data.reiskosten_onbelast} editing={editing} onChange={(v) => set({ reiskosten_onbelast: v })} type="number" low={allLow}
                 formatter={(v) => formatMoney(Number(v))} />
        </Row>
        <Row label="Functie, schaal, trede">
          <Field value={data.functie} editing={editing} onChange={(v) => set({ functie: v })} low={allLow} />
          {', schaal '}
          <Field value={data.schaal} editing={editing} onChange={(v) => set({ schaal: v })} type="number" low={allLow} />
          {', trede '}
          <Field value={data.trede} editing={editing} onChange={(v) => set({ trede: v })} type="number" low={allLow} />
        </Row>
        <Row label="Uurloon">
          <Field value={data.uurloon} editing={editing} onChange={(v) => set({ uurloon: v })} type="number" low={allLow}
                 formatter={(v) => formatMoney(Number(v))} />
        </Row>
        <Row label="Parttime %">
          <Field value={data.parttime_pct} editing={editing} onChange={(v) => set({ parttime_pct: v })} type="number" low={allLow}
                 formatter={(v) => `${v}%`} />
        </Row>
        <Row label="IBAN">
          <Field value={data.iban} editing={editing} onChange={(v) => set({ iban: v })} low={allLow} />
        </Row>
      </div>

      <div>
        <SectionLabel title="Looncomponenten" count={components.length} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]">
            <thead className="text-left text-xs text-slate-400 border-b border-surface-line/60">
              <tr>
                <th className="py-2 pr-2 font-medium">Omschrijving</th>
                <th className="py-2 px-2 text-right font-medium">Aantal</th>
                <th className="py-2 px-2 text-right font-medium">Basis</th>
                <th className="py-2 px-2 text-right font-medium">Normaal</th>
                <th className="py-2 px-2 text-right font-medium">Bijzonder</th>
                <th className="py-2 px-2 text-right font-medium">Cumulatief</th>
                <th className="py-2 pl-2 font-medium">Type</th>
              </tr>
            </thead>
            <tbody>
              {components.map((c: any, i: number) => {
                const low = allLow || c.confidence === 'low';
                return (
                  <tr key={i} className="border-b border-surface-line/60 last:border-0">
                    <td className="py-1.5 pr-2">
                      <Field value={c.omschrijving} editing={editing} onChange={(v) => setComponent(i, { omschrijving: v })} low={low} />
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      <Field value={c.aantal} editing={editing} onChange={(v) => setComponent(i, { aantal: v })} type="number" low={low} align="right" />
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      <Field value={c.basis} editing={editing} onChange={(v) => setComponent(i, { basis: v })} type="number" low={low} align="right"
                             formatter={(v) => (v == null ? '—' : Number(v).toFixed(2).replace('.', ','))} />
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      <Field value={c.normaal} editing={editing} onChange={(v) => setComponent(i, { normaal: v })} type="number" low={low} align="right"
                             formatter={(v) => (v == null ? '—' : Number(v).toFixed(2).replace('.', ','))} />
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      <Field value={c.bijzonder} editing={editing} onChange={(v) => setComponent(i, { bijzonder: v })} type="number" low={low} align="right"
                             formatter={(v) => (v == null ? '—' : Number(v).toFixed(2).replace('.', ','))} />
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      <Field value={c.cumulatief} editing={editing} onChange={(v) => setComponent(i, { cumulatief: v })} type="number" low={low} align="right"
                             formatter={(v) => (v == null ? '—' : Number(v).toFixed(2).replace('.', ','))} />
                    </td>
                    <td className="py-1.5 pl-2 text-slate-500">
                      <Field value={c.line_type} editing={editing} onChange={(v) => setComponent(i, { line_type: v })} low={low} />
                    </td>
                  </tr>
                );
              })}
              {components.length === 0 && (
                <tr><td colSpan={7} className="py-3 text-center text-slate-400">Geen looncomponenten</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ==================================================================== */
/* Urenspec                                                              */
/* ==================================================================== */

function UrenspecView({ data, editing, setData, allLow }: {
  data: any; editing: boolean; setData: (d: any) => void; allLow: boolean;
}) {
  const set = (patch: Partial<any>) => setData({ ...data, ...patch });
  const setLine = (idx: number, patch: Partial<any>) => {
    const lines = [...(data.lines ?? [])];
    lines[idx] = { ...lines[idx], ...patch };
    setData({ ...data, lines });
  };
  const lines = data.lines ?? [];

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-surface-line px-3">
        <Row label="Periode">
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <Field value={data.period_number} editing={editing} onChange={(v) => set({ period_number: v })} type="number" low={allLow} />
            <span className="text-slate-500">van</span>
            <Field value={data.year} editing={editing} onChange={(v) => set({ year: v })} type="number" low={allLow} />
          </span>
        </Row>
        <Row label="Totaal uren">
          <Field value={data.totaal_uren} editing={editing} onChange={(v) => set({ totaal_uren: v })} type="number" low={allLow}
                 formatter={(v) => formatHours(Number(v))} />
        </Row>
        <Row label="Totaal km">
          <Field value={data.totaal_km} editing={editing} onChange={(v) => set({ totaal_km: v })} type="number" low={allLow}
                 formatter={(v) => `${Number(v).toFixed(2).replace('.', ',')} km`} />
        </Row>
        <Row label="Wettelijk saldo">
          <Field value={data.wettelijk_saldo} editing={editing} onChange={(v) => set({ wettelijk_saldo: v })} type="number" low={allLow}
                 formatter={(v) => formatHours(Number(v))} />
          {', geldig tot '}
          <Field value={data.wettelijk_geldig_tot} editing={editing} onChange={(v) => set({ wettelijk_geldig_tot: v })} type="date" low={allLow}
                 formatter={(v) => formatDate(v)} />
        </Row>
        <Row label="Bovenwettelijk saldo">
          <Field value={data.bovenwettelijk_saldo} editing={editing} onChange={(v) => set({ bovenwettelijk_saldo: v })} type="number" low={allLow}
                 formatter={(v) => formatHours(Number(v))} />
          {', geldig tot '}
          <Field value={data.bovenwettelijk_geldig_tot} editing={editing} onChange={(v) => set({ bovenwettelijk_geldig_tot: v })} type="date" low={allLow}
                 formatter={(v) => formatDate(v)} />
        </Row>
      </div>

      <div>
        <SectionLabel title="Regels" count={lines.length} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]">
            <thead className="text-left text-xs text-slate-400 border-b border-surface-line/60">
              <tr>
                <th className="py-2 pr-2 font-medium">Dag</th>
                <th className="py-2 px-2 font-medium">Datum</th>
                <th className="py-2 px-2 font-medium">Activiteit</th>
                <th className="py-2 px-2 font-medium">Start</th>
                <th className="py-2 px-2 font-medium">Eind</th>
                <th className="py-2 px-2 text-right font-medium">Uren</th>
                <th className="py-2 px-2 font-medium">Object</th>
                <th className="py-2 px-2 text-right font-medium">Km</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l: any, i: number) => {
                const low = allLow || l.confidence === 'low';
                return (
                  <tr key={i} className="border-b border-surface-line/60 last:border-0">
                    <td className="py-1.5 pr-2 text-slate-400">
                      <Field value={l.day_abbr} editing={editing} onChange={(v) => setLine(i, { day_abbr: v })} low={low} />
                    </td>
                    <td className="py-1.5 px-2">
                      <Field value={l.date} editing={editing} onChange={(v) => setLine(i, { date: v })} type="date" low={low}
                             formatter={(v) => formatDate(v, 'dd-MM')} />
                    </td>
                    <td className="py-1.5 px-2">
                      <Field value={l.activity} editing={editing} onChange={(v) => setLine(i, { activity: v })} low={low} />
                    </td>
                    <td className="py-1.5 px-2">
                      <Field value={l.start} editing={editing} onChange={(v) => setLine(i, { start: v })} low={low} />
                    </td>
                    <td className="py-1.5 px-2">
                      <Field value={l.end} editing={editing} onChange={(v) => setLine(i, { end: v })} low={low} />
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      <Field value={l.hours} editing={editing} onChange={(v) => setLine(i, { hours: v })} type="number" low={low} align="right"
                             formatter={(v) => (v == null ? '—' : Number(v).toFixed(2).replace('.', ','))} />
                    </td>
                    <td className="py-1.5 px-2 text-slate-400 truncate max-w-[220px]">
                      <Field value={l.objectnaam} editing={editing} onChange={(v) => setLine(i, { objectnaam: v })} low={low} />
                    </td>
                    <td className="py-1.5 px-2 text-right text-slate-400">
                      <Field value={l.km1} editing={editing} onChange={(v) => setLine(i, { km1: v })} type="number" low={low} align="right" />
                    </td>
                  </tr>
                );
              })}
              {lines.length === 0 && (
                <tr><td colSpan={8} className="py-3 text-center text-slate-400">Geen regels</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ==================================================================== */
/* Urencontrole                                                          */
/* ==================================================================== */

function UrencontroleView({ data, editing, setData, allLow }: {
  data: any; editing: boolean; setData: (d: any) => void; allLow: boolean;
}) {
  const set = (patch: Partial<any>) => setData({ ...data, ...patch });
  const setLine = (idx: number, patch: Partial<any>) => {
    const lines = [...(data.lines ?? [])];
    lines[idx] = { ...lines[idx], ...patch };
    setData({ ...data, lines });
  };
  const lines = data.lines ?? [];

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-surface-line px-3">
        <Row label="Periode">
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <Field value={data.period_number} editing={editing} onChange={(v) => set({ period_number: v })} type="number" low={allLow} />
            <span className="text-slate-500">van</span>
            <Field value={data.year} editing={editing} onChange={(v) => set({ year: v })} type="number" low={allLow} />
          </span>
        </Row>
        <Row label="Weken">
          <Field value={data.from_week} editing={editing} onChange={(v) => set({ from_week: v })} type="number" low={allLow} />
          {' t/m '}
          <Field value={data.to_week} editing={editing} onChange={(v) => set({ to_week: v })} type="number" low={allLow} />
        </Row>
        <Row label="Totaal uren">
          <Field value={data.totaal_uren} editing={editing} onChange={(v) => set({ totaal_uren: v })} type="number" low={allLow}
                 formatter={(v) => formatHours(Number(v))} />
        </Row>
        <Row label="Aangemaakt op">
          <Field value={data.gegenereerd_op} editing={editing} onChange={(v) => set({ gegenereerd_op: v })} low={allLow} />
        </Row>
      </div>

      <div>
        <SectionLabel title="Regels" count={lines.length} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]">
            <thead className="text-left text-xs text-slate-400 border-b border-surface-line/60">
              <tr>
                <th className="py-2 pr-2 font-medium">Dag</th>
                <th className="py-2 px-2 font-medium">Datum</th>
                <th className="py-2 px-2 font-medium">Object</th>
                <th className="py-2 px-2 font-medium">Code</th>
                <th className="py-2 px-2 font-medium">Start</th>
                <th className="py-2 px-2 font-medium">Eind</th>
                <th className="py-2 px-2 text-right font-medium">Uren</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l: any, i: number) => {
                const low = allLow || l.confidence === 'low';
                return (
                  <tr key={i} className="border-b border-surface-line/60 last:border-0">
                    <td className="py-1.5 pr-2 text-slate-400">
                      <Field value={l.day_abbr} editing={editing} onChange={(v) => setLine(i, { day_abbr: v })} low={low} />
                    </td>
                    <td className="py-1.5 px-2">
                      <Field value={l.date} editing={editing} onChange={(v) => setLine(i, { date: v })} type="date" low={low}
                             formatter={(v) => formatDate(v, 'dd-MM')} />
                    </td>
                    <td className="py-1.5 px-2 truncate max-w-[220px]">
                      <Field value={l.omschrijving} editing={editing} onChange={(v) => setLine(i, { omschrijving: v })} low={low} />
                    </td>
                    <td className="py-1.5 px-2">
                      <Field value={l.bijz_code} editing={editing} onChange={(v) => setLine(i, { bijz_code: v })} low={low} />
                    </td>
                    <td className="py-1.5 px-2">
                      <Field value={l.start} editing={editing} onChange={(v) => setLine(i, { start: v })} low={low} />
                    </td>
                    <td className="py-1.5 px-2">
                      <Field value={l.end} editing={editing} onChange={(v) => setLine(i, { end: v })} low={low} />
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      <Field value={l.hours} editing={editing} onChange={(v) => setLine(i, { hours: v })} type="number" low={low} align="right"
                             formatter={(v) => (v == null ? '—' : Number(v).toFixed(2).replace('.', ','))} />
                    </td>
                  </tr>
                );
              })}
              {lines.length === 0 && (
                <tr><td colSpan={7} className="py-3 text-center text-slate-400">Geen regels</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ==================================================================== */
/* Contract / Addendum / Functiewijziging                                */
/* ==================================================================== */

function ContractView({ data, editing, setData, allLow }: {
  data: any; editing: boolean; setData: (d: any) => void; allLow: boolean;
}) {
  const set = (patch: Partial<any>) => setData({ ...data, ...patch });
  return (
    <div className="rounded-lg border border-surface-line px-3">
      <Row label="Ingangsdatum">
        <Field value={data.event_date} editing={editing} onChange={(v) => set({ event_date: v })} type="date" low={allLow}
               formatter={(v) => formatDate(v)} />
      </Row>
      <Row label="Soort wijziging">
        <Field value={data.event_type} editing={editing} onChange={(v) => set({ event_type: v })} low={allLow} />
      </Row>
      <Row label="Functie">
        <Field value={data.functie} editing={editing} onChange={(v) => set({ functie: v })} low={allLow} />
      </Row>
      <Row label="Loonschaal">
        {'schaal '}
        <Field value={data.schaal} editing={editing} onChange={(v) => set({ schaal: v })} type="number" low={allLow} />
        {', trede '}
        <Field value={data.trede} editing={editing} onChange={(v) => set({ trede: v })} type="number" low={allLow} />
      </Row>
      <Row label="Uurloon">
        <Field value={data.uurloon} editing={editing} onChange={(v) => set({ uurloon: v })} type="number" low={allLow}
               formatter={(v) => formatMoney(Number(v))} />
      </Row>
      <Row label="Uren / periode">
        <Field value={data.uren_periode} editing={editing} onChange={(v) => set({ uren_periode: v })} type="number" low={allLow}
               formatter={(v) => formatHours(Number(v))} />
      </Row>
      <Row label="Parttime %">
        <Field value={data.parttime_pct} editing={editing} onChange={(v) => set({ parttime_pct: v })} type="number" low={allLow}
               formatter={(v) => `${v}%`} />
      </Row>
      <Row label="Soort contract">
        <Field value={data.contract_type} editing={editing} onChange={(v) => set({ contract_type: v })} low={allLow} />
      </Row>
      <Row label="Model">
        <Field value={data.model} editing={editing} onChange={(v) => set({ model: v })} low={allLow} />
      </Row>
      <Row label="Einddatum">
        <Field value={data.end_date} editing={editing} onChange={(v) => set({ end_date: v })} type="date" low={allLow}
               formatter={(v) => formatDate(v)} />
      </Row>
      <Row label="Notities">
        <Field value={data.notes} editing={editing} onChange={(v) => set({ notes: v })} low={allLow} />
      </Row>
    </div>
  );
}

/* ==================================================================== */
/* Myrostar                                                              */
/* ==================================================================== */

function MyrostarView({ data, editing, setData, allLow }: {
  data: any; editing: boolean; setData: (d: any) => void; allLow: boolean;
}) {
  const set = (patch: Partial<any>) => setData({ ...data, ...patch });
  const setShift = (idx: number, patch: Partial<any>) => {
    const shifts = [...(data.shifts ?? [])];
    shifts[idx] = { ...shifts[idx], ...patch };
    setData({ ...data, shifts });
  };
  const shifts = data.shifts ?? [];

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-surface-line px-3">
        <Row label="Week">
          <Field value={data.year} editing={editing} onChange={(v) => set({ year: v })} type="number" low={allLow} />
          {', week '}
          <Field value={data.week} editing={editing} onChange={(v) => set({ week: v })} type="number" low={allLow} />
        </Row>
        <Row label="Van t/m">
          <Field value={data.from_date} editing={editing} onChange={(v) => set({ from_date: v })} type="date" low={allLow}
                 formatter={(v) => formatDate(v)} />
          {' t/m '}
          <Field value={data.to_date} editing={editing} onChange={(v) => set({ to_date: v })} type="date" low={allLow}
                 formatter={(v) => formatDate(v)} />
        </Row>
      </div>

      <div>
        <SectionLabel title="Diensten" count={shifts.length} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]">
            <thead className="text-left text-xs text-slate-400 border-b border-surface-line/60">
              <tr>
                <th className="py-2 pr-2 font-medium">Dag</th>
                <th className="py-2 px-2 font-medium">Datum</th>
                <th className="py-2 px-2 font-medium">Start</th>
                <th className="py-2 px-2 font-medium">Eind</th>
                <th className="py-2 px-2 font-medium">Object</th>
                <th className="py-2 px-2 font-medium">Locatie</th>
                <th className="py-2 px-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {shifts.map((s: any, i: number) => {
                const low = allLow || s.confidence === 'low';
                return (
                  <tr key={i} className="border-b border-surface-line/60 last:border-0">
                    <td className="py-1.5 pr-2 text-slate-400">
                      <Field value={s.day_abbr} editing={editing} onChange={(v) => setShift(i, { day_abbr: v })} low={low} />
                    </td>
                    <td className="py-1.5 px-2">
                      <Field value={s.date} editing={editing} onChange={(v) => setShift(i, { date: v })} type="date" low={low}
                             formatter={(v) => formatDate(v, 'dd-MM')} />
                    </td>
                    <td className="py-1.5 px-2">
                      <Field value={s.start} editing={editing} onChange={(v) => setShift(i, { start: v })} low={low} />
                    </td>
                    <td className="py-1.5 px-2">
                      <Field value={s.end} editing={editing} onChange={(v) => setShift(i, { end: v })} low={low} />
                    </td>
                    <td className="py-1.5 px-2 truncate max-w-[200px]">
                      <Field value={s.object_name} editing={editing} onChange={(v) => setShift(i, { object_name: v })} low={low} />
                    </td>
                    <td className="py-1.5 px-2 text-slate-400">
                      <Field value={s.location} editing={editing} onChange={(v) => setShift(i, { location: v })} low={low} />
                    </td>
                    <td className="py-1.5 px-2 text-slate-400">
                      <Field value={s.status} editing={editing} onChange={(v) => setShift(i, { status: v })} low={low} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ==================================================================== */
/* Generic fallback                                                      */
/* ==================================================================== */

function GenericJsonView({ data, editing, setData }: { data: any; editing: boolean; setData: (d: any) => void }) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (editing) setDraft(JSON.stringify(data, null, 2));
  }, [editing, data]);

  const text = useMemo(() => (editing ? draft : JSON.stringify(data, null, 2)), [editing, draft, data]);

  if (!editing) {
    return (
      <pre className="text-xs text-slate-300 bg-slate-950/60 border border-surface-line p-3 rounded-lg overflow-auto max-h-[60vh] whitespace-pre-wrap break-words">
        {text}
      </pre>
    );
  }

  return (
    <div className="space-y-2">
      <textarea
        className="input font-mono text-xs min-h-[300px]"
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          try {
            const parsed = JSON.parse(e.target.value);
            setData(parsed);
            setError(null);
          } catch (err: any) {
            setError(err.message);
          }
        }}
        spellCheck={false}
      />
      {error && <p className="text-sm text-red-300">Ongeldige invoer: {error}</p>}
      <p className="text-sm text-slate-400">
        Na opslaan geldt dit document als gecontroleerd.
      </p>
    </div>
  );
}
